import { useEffect, useState } from "react";
import { Button, Card, Typography, message } from "antd";
import {
  CheckOutlined,
  EditOutlined,
  EyeOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import olreadiaService from "services/olreadiaService";

const { Text, Title } = Typography;

const COLORS = {
  green: "#27ae30",
  darkGreen: "#14532d",
  greenSoft: "#e7f6e8",
  blue: "#1a56db",
  darkBlue: "#0f172a",
  textMuted: "#8a938c",
};

const GEN_STEPS = [
  {
    id: "analyze",
    label: "Analysing your details",
    icon: "🔍",
    duration: 2000,
  },
  { id: "design", label: "Applying your template", icon: "🎨", duration: 2500 },
  {
    id: "assets",
    label: "Optimising assets & images",
    icon: "🖼️",
    duration: 2000,
  },
  { id: "deploy", label: "Deploying your website", icon: "🚀", duration: 1500 },
];

const CONFETTI = [
  { x: "10%", color: COLORS.green, size: 10, delay: 0 },
  { x: "25%", color: COLORS.blue, size: 7, delay: 100 },
  { x: "40%", color: "#f59e0b", size: 12, delay: 200 },
  { x: "55%", color: COLORS.green, size: 8, delay: 50 },
  { x: "70%", color: COLORS.darkBlue, size: 9, delay: 150 },
  { x: "85%", color: "#e11d48", size: 7, delay: 250 },
  { x: "18%", color: "#8b5cf6", size: 11, delay: 300 },
  { x: "62%", color: COLORS.green, size: 6, delay: 80 },
  { x: "45%", color: "#f59e0b", size: 8, delay: 350 },
];

// one-time global keyframes
function ModalKeyframes() {
  return (
    <style>{`
      @keyframes wgm-fade-slide-in {
        from { opacity: 0; transform: translateY(12px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes wgm-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @keyframes wgm-pulse {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }
      @keyframes wgm-badge-pop {
        0% { transform: scale(0); }
        70% { transform: scale(1.08); }
        100% { transform: scale(1); }
      }
      @keyframes wgm-confetti-fall {
        0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
        10% { opacity: 1; }
        100% { transform: translateY(320px) rotate(540deg); opacity: 0; }
      }
    `}</style>
  );
}

// generating step
function GenStepRow({ icon, label, state, delay }) {
  const bg =
    state === "done"
      ? COLORS.greenSoft
      : state === "active"
        ? "#e9f1fb"
        : "#f8fafc";
  const border =
    state === "done"
      ? COLORS.green
      : state === "active"
        ? COLORS.blue
        : "#e5e7eb";
  const labelColor =
    state === "done"
      ? COLORS.darkGreen
      : state === "active"
        ? "#1f4a73"
        : COLORS.textMuted;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px",
        borderRadius: 12,
        border: `1px solid ${border}`,
        background: bg,
        animation: "wgm-fade-slide-in 300ms ease both",
        animationDelay: `${delay}ms`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "rgba(255,255,255,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
      </div>
      <Text
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: state === "active" ? 600 : 500,
          color: labelColor,
        }}
      >
        {label}
      </Text>
      <div style={{ width: 22, display: "flex", justifyContent: "center" }}>
        {state === "done" && (
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 10,
              background: COLORS.green,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckOutlined style={{ fontSize: 10, color: "#fff" }} />
          </div>
        )}
        {state === "active" && (
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 5,
              background: COLORS.blue,
              animation: "wgm-pulse 1.2s ease-in-out infinite",
            }}
          />
        )}
        {state === "pending" && (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: "#e5e7eb",
            }}
          />
        )}
      </div>
    </div>
  );
}

// generating
function StepGenerating({
  run,
  errorMessage,
  onDone,
  onError,
  onRetry,
  onClose,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [stepStates, setStepStates] = useState(
    GEN_STEPS.map((_, i) => (i === 0 ? "active" : "pending")),
  );
  const [progressPct, setProgressPct] = useState(0);
  const [transitionMs, setTransitionMs] = useState(GEN_STEPS[0].duration);

  useEffect(() => {
    let cancelled = false;
    let stepIndex = 0;

    const advanceStep = () => {
      if (cancelled || stepIndex >= GEN_STEPS.length) return;
      const current = stepIndex;
      setActiveStep(current);
      setStepStates((prev) =>
        prev.map((_, i) =>
          i < current ? "done" : i === current ? "active" : "pending",
        ),
      );
      setTransitionMs(GEN_STEPS[current].duration);
      setProgressPct(((current + 1) / GEN_STEPS.length) * 100);

      if (current < GEN_STEPS.length - 1) {
        setTimeout(() => {
          stepIndex += 1;
          advanceStep();
        }, GEN_STEPS[current].duration);
      }
    };
    advanceStep();

    const totalDuration = GEN_STEPS.reduce((sum, s) => sum + s.duration, 0);
    const startTime = Date.now();

    (async () => {
      try {
        const result = await run();
        const remaining = Math.max(0, totalDuration - (Date.now() - startTime));
        setTimeout(() => {
          if (cancelled) return;
          setStepStates(GEN_STEPS.map(() => "done"));
          setTimeout(() => !cancelled && onDone(result), 500);
        }, remaining);
      } catch (err) {
        if (cancelled) return;
        onError(err?.message || "Something went wrong. Please try again.");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (errorMessage) {
    return (
      <div style={{ textAlign: "center", padding: "32px 12px" }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
        <Title level={5} style={{ margin: "0 0 6px", color: COLORS.darkGreen }}>
          Generation failed
        </Title>
        <Text
          style={{
            fontSize: 13,
            color: COLORS.textMuted,
            display: "block",
            marginBottom: 18,
          }}
        >
          {errorMessage}
        </Text>
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Button onClick={onClose} style={{ borderRadius: 999 }}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={onRetry}
            style={{
              borderRadius: 999,
              background: COLORS.green,
              borderColor: COLORS.green,
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const progressPercentLabel = Math.round(
    ((activeStep + 1) / GEN_STEPS.length) * 100,
  );

  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            background: COLORS.greenSoft,
            border: `3px solid ${COLORS.green}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ThunderboltOutlined
            style={{
              fontSize: 24,
              color: COLORS.green,
              animation: "wgm-spin 3s linear infinite",
            }}
          />
        </div>
        <Title
          level={5}
          style={{ margin: 0, color: COLORS.darkGreen, textAlign: "center" }}
        >
          Building your website
        </Title>
        <Text
          style={{ fontSize: 12, color: COLORS.textMuted, textAlign: "center" }}
        >
          Sit tight — this usually takes 15–20 seconds
        </Text>

        <div
          style={{
            width: "100%",
            height: 6,
            background: "#e5e7eb",
            borderRadius: 999,
            overflow: "hidden",
            marginTop: 4,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progressPct}%`,
              background: COLORS.green,
              borderRadius: 999,
              transition: `width ${transitionMs}ms linear`,
            }}
          />
        </div>
        <Text style={{ fontSize: 11, fontWeight: 600, color: COLORS.green }}>
          {progressPercentLabel}% complete
        </Text>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {GEN_STEPS.map((s, i) => (
          <GenStepRow
            key={s.id}
            icon={s.icon}
            label={s.label}
            state={stepStates[i]}
            delay={i * 120}
          />
        ))}
      </div>

      <Text
        style={{
          fontSize: 11,
          color: COLORS.textMuted,
          display: "block",
          textAlign: "center",
          marginTop: 18,
        }}
      >
        🔒 Your data is encrypted and securely processed
      </Text>
    </div>
  );
}

// steps
function ConfettiDot({ delay, x, color, size }) {
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: 0,
        width: size,
        height: size,
        borderRadius: size / 4,
        background: color,
        animation: "wgm-confetti-fall 2000ms ease-in both",
        animationDelay: `${delay}ms`,
      }}
    />
  );
}

function ActionCard({
  icon,
  title,
  subtitle,
  onClick,
  variant = "secondary",
  delay,
}) {
  const bg = variant === "primary" ? COLORS.green : "#fff";
  const border = variant === "secondary" ? "#e5e7eb" : "transparent";
  const textColor = variant === "primary" ? "#fff" : "#1a1a1a";
  const subColor =
    variant === "primary" ? "rgba(255,255,255,0.75)" : COLORS.textMuted;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 12,
        border: `1px solid ${border}`,
        background: bg,
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        animation: "wgm-fade-slide-in 300ms ease both",
        animationDelay: `${delay}ms`,
      }}
    >
      <span style={{ fontSize: 20, color: textColor }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 700,
            color: textColor,
          }}
        >
          {title}
        </Text>
        <Text style={{ fontSize: 11, color: subColor }}>{subtitle}</Text>
      </span>
      <span style={{ color: textColor, fontWeight: 700 }}>→</span>
    </button>
  );
}

function StepSuccess({ result, previewUrl, template, onDone, onEdit }) {
  const displayUrl = previewUrl.replace(/^https?:\/\//, "");

  const handlePreview = () =>
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  const handleEdit = () => onEdit(result);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My new website",
          text: `Check out my new website: ${previewUrl}`,
          url: previewUrl,
        });
      } catch {
        /* user cancelled — no-op */
      }
    } else {
      await navigator.clipboard.writeText(previewUrl);
      message.success("Link copied to clipboard");
    }
  };

  return (
    <div style={{ position: "relative", marginBottom: 10 }}>
      <div
        style={{
          position: "absolute",
          inset: "0 0 auto 0",
          height: 320,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {CONFETTI.map((c, i) => (
          <ConfettiDot key={i} {...c} />
        ))}
      </div>

      <div
        style={{ textAlign: "center", marginBottom: 20, position: "relative" }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            background: COLORS.greenSoft,
            border: `3px solid ${COLORS.green}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 10px",
            animation: "wgm-badge-pop 500ms ease",
          }}
        >
          <span style={{ fontSize: 28 }}>🎉</span>
        </div>
        <Title level={4} style={{ margin: "0 0 6px", color: COLORS.darkGreen }}>
          Your website is live!
        </Title>
        <Text
          style={{
            fontSize: 13,
            color: COLORS.textMuted,
            display: "block",
            width: "80%",
            margin: "0 auto",
          }}
        >
          We've generated a complete website using the{" "}
          <Text style={{ color: COLORS.green, fontWeight: 600 }}>
            {template?.name || "selected"}
          </Text>{" "}
          template. Start editing to make it yours.
        </Text>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: COLORS.darkBlue,
            borderRadius: 10,
            padding: "10px 14px",
            marginTop: 14,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: COLORS.green,
              flexShrink: 0,
            }}
          />
          <Text
            style={{
              flex: 1,
              fontSize: 12,
              fontFamily: "monospace",
              color: "rgba(255,255,255,0.85)",
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayUrl}
          </Text>
          <Button
            size="small"
            onClick={handlePreview}
            style={{
              borderRadius: 999,
              background: COLORS.green,
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            Open ↗
          </Button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <ActionCard
          icon={<EyeOutlined />}
          title="Preview website"
          subtitle="See your live website in the browser"
          onClick={handlePreview}
          variant="primary"
          delay={100}
        />
        <ActionCard
          icon={<EditOutlined />}
          title="Edit website"
          subtitle="Customise content, colors and layout"
          onClick={handleEdit}
          delay={180}
        />
        <ActionCard
          icon={<ShareAltOutlined />}
          title="Share"
          subtitle="Send the preview link to someone"
          onClick={handleShare}
          delay={260}
        />
      </div>

      <Text
        onClick={onDone}
        style={{
          fontSize: 14,
          fontWeight: 600,
          textAlign: "center",
          color: COLORS.textMuted,
          display: "block",
          cursor: "pointer",
        }}
      >
        Close
      </Text>
    </div>
  );
}

// main modal
export default function WebsiteGenerateModal({
  open,
  onClose,
  template,
  run,
  onEdit,
  getPreviewUrl,
}) {
  const [prevOpen, setPrevOpen] = useState(open);
  const [step, setStep] = useState("generating"); // "generating" | "success"
  const [result, setResult] = useState(null);
  const [genError, setGenError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setStep("generating");
      setResult(null);
      setGenError(null);
      setRetryToken((t) => t + 1);
    }
  }

  // const allowClose = step === "success" || Boolean(genError);

  // useEffect(() => {
  //   const handler = (e) => e.key === "Escape" && allowClose && onClose();
  //   if (open) window.addEventListener("keydown", handler);
  //   return () => window.removeEventListener("keydown", handler);
  // }, [open, onClose, allowClose]);

  // if (!open) return null;

  const allowClose = step === "success" || Boolean(genError);

  // Only pass the result along if generation actually succeeded —
  // an error-state close shouldn't look like a success close.
  const closeWithResult = () => onClose(step === "success" ? result : null);

  useEffect(() => {
    const handler = (e) =>
      e.key === "Escape" && allowClose && closeWithResult();
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allowClose, step, result]);

  if (!open) return null;

  return (
    <div
      // onClick={allowClose ? onClose : undefined}
      onClick={allowClose ? closeWithResult : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.38)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <ModalKeyframes />
      <Card
        onClick={(e) => e.stopPropagation()}
        variant="borderless"
        style={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 16,
          border: `1px solid ${COLORS.greenSoft}`,
          boxShadow: "0 24px 64px rgba(0,64,56,0.14)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: "26px 28px 22px" } }}
      >
        {step === "generating" && (
          <StepGenerating
            key={retryToken}
            run={run}
            errorMessage={genError}
            onDone={(res) => {
              setResult(res);
              setStep("success");
            }}
            onError={(msg) => setGenError(msg)}
            onRetry={() => {
              setGenError(null);
              setRetryToken((t) => t + 1);
            }}
            onClose={onClose}
          />
        )}
        {step === "success" && (
          <StepSuccess
            result={result}
            previewUrl={olreadiaService.websites.previewUrl(result?.slug)}
            template={template}
            // onDone={onClose}
            onDone={() => onClose(result)} 
            onEdit={onEdit}
          />
        )}
      </Card>
    </div>
  );
}
